import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ChevronRight, RotateCcw, Save } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Field, Input, Toggle } from '@/components/ui/Input';
import { ConfirmDialog } from '@/components/ui/Modal';
import { PageLoader } from '@/components/ui/States';
import { useToast } from '@/components/ui/Toast';
import { formatBytes, formatIdr, formatNumber } from '@/lib/format';
import { cn } from '@/lib/cn';

/**
 * Penyunting paket.
 *
 * Dulu halaman ini hanya bisa mengubah harga dan tagline, karena mengubah
 * batas layanan lewat formulir adalah cara tercepat merusak akun pelanggan
 * yang sedang berjalan. Sekarang seluruhnya bisa diubah, dan kekhawatiran itu
 * dijawab dengan pengaman, bukan larangan:
 *
 *   1. Server menolak penurunan batas di bawah pemakaian pelanggan yang
 *      sedang berjalan, dan menyebutkan siapa saja yang akan terkunci.
 *      Penekan tombol harus menyatakan sadar sebelum itu bisa dilewati.
 *   2. Tiap perubahan tercatat di audit log beserta nilai sebelum dan
 *      sesudahnya.
 */

const FREKUENSI = [
  ['hourly', 'Per jam'],
  ['daily', 'Harian'],
  ['weekly', 'Mingguan'],
  ['monthly', 'Bulanan'],
];

/** Penyimpanan disimpan dalam byte, tetapi diisi orang dalam MB. */
const keMb = (byte) => Math.round((byte / (1024 * 1024)) * 10) / 10;
const keByte = (mb) => Math.round(Number(mb) * 1024 * 1024);

const AngkaField = ({ label, hint, nilai, onChange, suffix }) => (
  <Field label={label} hint={hint}>
    {(props) => (
      <div className="relative">
        <Input
          {...props}
          type="number"
          min="0"
          value={nilai}
          onChange={(e) => onChange(e.target.value)}
          className={suffix ? 'pr-12' : undefined}
        />
        {suffix && (
          <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs text-[var(--text-muted)]">
            {suffix}
          </span>
        )}
      </div>
    )}
  </Field>
);

const PenyuntingPaket = ({ plan, onSimpan, menyimpan, onKembalikan, terbuka, onToggle }) => {
  const [f, setF] = useState({
    name: plan.name,
    priceIdr: String(plan.priceIdr),
    tagline: plan.tagline ?? '',
    periodDays: String(plan.periodDays),
    maxRecords: String(plan.maxRecords),
    maxJobs: String(plan.maxJobs),
    maxConcurrentRuns: String(plan.maxConcurrentRuns),
    maxPagesPerJob: String(plan.maxPagesPerJob),
    retentionDays: String(plan.retentionDays),
    storageMb: String(keMb(plan.storageBytes)),
    seats: String(plan.seats),
    scheduleAllowed: Boolean(plan.scheduleAllowed),
    scheduleFrequencies: plan.scheduleFrequencies ?? [],
    maxSchedules: String(plan.maxSchedules ?? 0),
    apiAccess: Boolean(plan.apiAccess),
    addOnAllowed: Boolean(plan.addOnAllowed),
    hidden: Boolean(plan.hidden),
  });

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const toggleFrekuensi = (kode) =>
    set(
      'scheduleFrequencies',
      f.scheduleFrequencies.includes(kode)
        ? f.scheduleFrequencies.filter((x) => x !== kode)
        : [...f.scheduleFrequencies, kode],
    );

  const kirim = () =>
    onSimpan(plan.id, {
      name: f.name,
      priceIdr: Number(f.priceIdr),
      tagline: f.tagline,
      periodDays: Number(f.periodDays),
      maxRecords: Number(f.maxRecords),
      maxJobs: Number(f.maxJobs),
      maxConcurrentRuns: Number(f.maxConcurrentRuns),
      maxPagesPerJob: Number(f.maxPagesPerJob),
      retentionDays: Number(f.retentionDays),
      storageBytes: keByte(f.storageMb),
      seats: Number(f.seats),
      scheduleAllowed: f.scheduleAllowed,
      scheduleFrequencies: f.scheduleFrequencies,
      maxSchedules: Number(f.maxSchedules),
      apiAccess: f.apiAccess,
      addOnAllowed: f.addOnAllowed,
      hidden: f.hidden,
    });

  return (
    <Card>
      {/* Kepala kartu menjadi tombol buka-tutup. Lima paket yang semuanya
          terbuka menghasilkan halaman sepanjang belasan layar, dan untuk
          mengubah satu angka pada paket terakhir orang harus menggulir
          melewati empat paket yang tidak sedang diurusnya. */}
      <div className="flex items-start justify-between gap-4 border-b border-[var(--border-subtle)] px-5 py-4">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={terbuka}
          className="flex min-w-0 flex-1 items-start gap-3 text-left"
        >
          <ChevronRight
            className={cn(
              'mt-0.5 size-4 shrink-0 text-[var(--text-muted)] transition-transform duration-200',
              terbuka && 'rotate-90',
            )}
            aria-hidden="true"
          />
          <span className="min-w-0">
            <span className="flex flex-wrap items-center gap-2">
              <span className="text-[15px] font-bold text-[var(--text-primary)]">{plan.name}</span>
              <code className="rounded bg-[var(--surface-sunken)] px-1.5 py-0.5 font-mono text-xs text-[var(--text-muted)]">
                {plan.id}
              </code>
              {plan.isOverridden && (
                <Badge tone="warning" size="sm">
                  Diubah
                </Badge>
              )}
              {plan.hidden && (
                <Badge tone="neutral" size="sm">
                  Disembunyikan
                </Badge>
              )}
            </span>

            {/* Ringkasan nilai YANG BERLAKU, bukan nilai bawaan. Saat kartunya
                tertutup, baris inilah satu-satunya yang terbaca, jadi ia harus
                menjawab "paket ini sekarang seperti apa" tanpa dibuka. */}
            <span className="mt-0.5 block text-sm text-[var(--text-muted)]">
              {formatIdr(plan.priceIdr)} · {formatNumber(plan.maxRecords)} baris ·{' '}
              {formatBytes(plan.storageBytes)} · {formatNumber(plan.maxJobs)} job ·{' '}
              {plan.scheduleAllowed ? `${formatNumber(plan.maxSchedules)} jadwal` : 'tanpa jadwal'}
            </span>
          </span>
        </button>

        {plan.isOverridden && (
          <Button
            variant="ghost"
            size="sm"
            icon={RotateCcw}
            className="shrink-0"
            onClick={() => onKembalikan(plan)}
          >
            Kembalikan
          </Button>
        )}
      </div>

      {!terbuka ? null : (
      <CardBody className="space-y-5">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="Nama tampilan">
            {(props) => (
              <Input {...props} value={f.name} onChange={(e) => set('name', e.target.value)} />
            )}
          </Field>
          <AngkaField
            label="Harga"
            nilai={f.priceIdr}
            onChange={(v) => set('priceIdr', v)}
            suffix="Rp"
          />
          <AngkaField
            label="Lama periode"
            hint="30 hari berarti bulanan."
            nilai={f.periodDays}
            onChange={(v) => set('periodDays', v)}
            suffix="hari"
          />
        </div>

        <Field label="Tagline" hint='Kalimat singkat di kartu harga, mis. "Paling laris".'>
          {(props) => (
            <Input {...props} value={f.tagline} onChange={(e) => set('tagline', e.target.value)} />
          )}
        </Field>

        <div className="border-t border-[var(--border-subtle)] pt-4">
          <p className="mb-3 text-xs font-bold tracking-wide text-[var(--text-muted)] uppercase">
            Batas kuota
          </p>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <AngkaField label="Baris data" nilai={f.maxRecords} onChange={(v) => set('maxRecords', v)} />
            <AngkaField label="Jumlah job" nilai={f.maxJobs} onChange={(v) => set('maxJobs', v)} />
            <AngkaField
              label="Penyimpanan"
              nilai={f.storageMb}
              onChange={(v) => set('storageMb', v)}
              suffix="MB"
            />
            <AngkaField
              label="Crawl bersamaan"
              nilai={f.maxConcurrentRuns}
              onChange={(v) => set('maxConcurrentRuns', v)}
            />
            <AngkaField
              label="Halaman per job"
              nilai={f.maxPagesPerJob}
              onChange={(v) => set('maxPagesPerJob', v)}
            />
            <AngkaField
              label="Retensi data"
              nilai={f.retentionDays}
              onChange={(v) => set('retentionDays', v)}
              suffix="hari"
            />
            {/* Kursi anggota tidak lagi ditawarkan ke pelanggan, tetapi
                tetap bisa diatur di sini: batasnya masih ditegakkan server,
                dan menghapus kolomnya dari layar berarti angka lama membeku
                tanpa ada yang bisa menyentuhnya lagi. */}
            <AngkaField
              label="Kursi anggota"
              hint="Tidak ditampilkan di halaman harga"
              nilai={f.seats}
              onChange={(v) => set('seats', v)}
            />
          </div>
        </div>

        <div className="border-t border-[var(--border-subtle)] pt-4">
          <p className="mb-3 text-xs font-bold tracking-wide text-[var(--text-muted)] uppercase">
            Penjadwalan otomatis
          </p>
          <Toggle
            checked={f.scheduleAllowed}
            onChange={(v) => set('scheduleAllowed', v)}
            label="Boleh menjadwalkan"
            description="Kalau dimatikan, seluruh frekuensi ikut tidak tersedia."
          />

          {f.scheduleAllowed && (
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div>
                <p className="mb-2 text-sm font-semibold text-[var(--text-primary)]">
                  Frekuensi yang dijual
                </p>
                <div className="flex flex-wrap gap-2">
                  {FREKUENSI.map(([kode, label]) => {
                    const aktif = f.scheduleFrequencies.includes(kode);
                    return (
                      <button
                        key={kode}
                        type="button"
                        aria-pressed={aktif}
                        onClick={() => toggleFrekuensi(kode)}
                        className={cn(
                          'rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors',
                          aktif
                            ? 'border-accent-600 bg-accent-50 text-[var(--text-primary)] dark:bg-accent-500/10'
                            : 'border-[var(--border-subtle)] text-[var(--text-muted)] hover:border-[var(--border-default)]',
                        )}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
                <p className="mt-2 text-xs text-[var(--text-muted)]">
                  Per jam dan harian jauh lebih boros: satu job per jam berarti 720 crawl sebulan.
                </p>
              </div>
              <AngkaField
                label="Batas jadwal aktif"
                hint="Berapa job yang boleh punya jadwal menyala bersamaan."
                nilai={f.maxSchedules}
                onChange={(v) => set('maxSchedules', v)}
              />
            </div>
          )}
        </div>

        {/* Berkotak, bukan sekadar berjajar.

            Tanpa batas yang terlihat, sakelar milik kolom pertama berhenti
            tepat di sebelah kiri label kolom kedua, dan terbaca seolah milik
            label itu. */}
        <div className="grid gap-3 border-t border-[var(--border-subtle)] pt-4 sm:grid-cols-3">
          <Toggle
            kotak
            checked={f.apiAccess}
            onChange={(v) => set('apiAccess', v)}
            label="Akses API"
            description="Kunci API untuk integrasi."
          />
          <Toggle
            kotak
            checked={f.addOnAllowed}
            onChange={(v) => set('addOnAllowed', v)}
            label="Boleh beli add-on"
            description="Matikan di paket gratis agar add-on tidak jadi penggantinya."
          />
          <Toggle
            kotak
            checked={f.hidden}
            onChange={(v) => set('hidden', v)}
            label="Sembunyikan"
            description="Tidak tampil di halaman harga, tetapi tetap berlaku bagi yang sudah memakainya."
          />
        </div>

        <div className="flex justify-end border-t border-[var(--border-subtle)] pt-4">
          <Button icon={Save} loading={menyimpan} onClick={kirim}>
            Simpan
          </Button>
        </div>
      </CardBody>
      )}
    </Card>
  );
};

export const PlansAdminPage = () => {
  const toast = useToast();
  // Satu paket terbuka pada satu waktu. Membolehkan semuanya terbuka
  // mengembalikan halaman panjang yang justru sedang dihindari, dan tidak ada
  // pekerjaan yang menuntut membandingkan dua penyunting berdampingan:
  // ringkasan di kepala kartu sudah memuat angka yang berlaku.
  const [terbuka, setTerbuka] = useState(null);
  const queryClient = useQueryClient();
  const [konfirmasiPaksa, setKonfirmasiPaksa] = useState(null);
  const [konfirmasiKembali, setKonfirmasiKembali] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['platform-plans'],
    queryFn: () => api.get('/platform/plans'),
  });

  const segarkan = () => {
    queryClient.invalidateQueries({ queryKey: ['platform-plans'] });
    queryClient.invalidateQueries({ queryKey: ['public-plans'] });
    queryClient.invalidateQueries({ queryKey: ['usage'] });
  };

  const simpan = useMutation({
    mutationFn: ({ id, body }) => api.patch(`/platform/plans/${id}`, body),
    onSuccess: (res) => {
      toast.success('Paket disimpan', { description: `${res?.data?.name} diperbarui.` });
      setKonfirmasiPaksa(null);
      segarkan();
    },
    onError: (err, variabel) => {
      // 409 berarti ada pelanggan yang pemakaiannya sudah melebihi batas
      // baru. Bukan galat yang perlu ditakuti, tetapi keputusan yang harus
      // diambil sadar, jadi ditawarkan lagi lengkap dengan daftar korbannya.
      if (err instanceof ApiError && err.status === 409) {
        setKonfirmasiPaksa({ ...variabel, pesan: err.message, terdampak: err.details?.terdampak ?? [] });
        return;
      }
      toast.error('Gagal menyimpan', {
        description: err instanceof ApiError ? err.message : 'Coba lagi.',
      });
    },
  });

  const kembalikan = useMutation({
    mutationFn: (id) => api.delete(`/platform/plans/${id}`),
    onSuccess: (res) => {
      toast.success('Dikembalikan ke bawaan', { description: `${res?.data?.name} memakai nilai asli.` });
      setKonfirmasiKembali(null);
      segarkan();
    },
    onError: (err) =>
      toast.error('Gagal mengembalikan', {
        description: err instanceof ApiError ? err.message : 'Coba lagi.',
      }),
  });

  if (isLoading) return <PageLoader label="Memuat paket..." />;

  const paket = data?.data ?? [];

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-bold text-[var(--text-primary)]">Harga Paket</h1>
        <p className="mt-1 max-w-3xl text-sm text-[var(--text-secondary)]">
          Perubahan berlaku seketika tanpa perlu deploy. Tiap perubahan tercatat beserta nilai
          sebelum dan sesudahnya, dan penurunan batas di bawah pemakaian pelanggan yang sedang
          berjalan akan ditahan sampai Anda menyatakan sadar.
        </p>
      </div>

      {paket.map((plan) => (
        <PenyuntingPaket
          // Kunci memuat nilai-nilainya supaya formulir memuat ulang isinya
          // sesudah disimpan atau dikembalikan ke bawaan.
          key={`${plan.id}-${plan.priceIdr}-${plan.maxRecords}-${plan.storageBytes}-${plan.isOverridden}`}
          plan={plan}
          menyimpan={simpan.isPending}
          onSimpan={(id, body) => simpan.mutate({ id, body })}
          onKembalikan={setKonfirmasiKembali}
          terbuka={terbuka === plan.id}
          onToggle={() => setTerbuka(terbuka === plan.id ? null : plan.id)}
        />
      ))}

      <ConfirmDialog
        open={Boolean(konfirmasiPaksa)}
        onClose={() => setKonfirmasiPaksa(null)}
        onConfirm={() =>
          simpan.mutate({ id: konfirmasiPaksa.id, body: { ...konfirmasiPaksa.body, paksa: true } })
        }
        loading={simpan.isPending}
        destructive
        title="Ada pelanggan yang akan terkunci"
        confirmLabel="Saya sadar, tetap simpan"
        description={
          <span className="block space-y-2">
            <span className="flex gap-2">
              <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warning-600" aria-hidden="true" />
              <span>{konfirmasiPaksa?.pesan}</span>
            </span>
            {konfirmasiPaksa?.terdampak?.length > 0 && (
              <span className="mt-2 block rounded-lg bg-[var(--surface-sunken)] p-3 text-xs">
                {konfirmasiPaksa.terdampak.map((t) => (
                  <span key={t.id} className="block">
                    {t.nama} <span className="text-[var(--text-muted)]">({t.keterangan})</span>
                  </span>
                ))}
              </span>
            )}
            <span className="mt-2 block text-xs text-[var(--text-muted)]">
              Mereka tidak akan bisa mengumpulkan data baru sampai pemakaiannya turun atau paketnya
              dinaikkan.
            </span>
          </span>
        }
      />

      <ConfirmDialog
        open={Boolean(konfirmasiKembali)}
        onClose={() => setKonfirmasiKembali(null)}
        onConfirm={() => kembalikan.mutate(konfirmasiKembali.id)}
        loading={kembalikan.isPending}
        title={`Kembalikan ${konfirmasiKembali?.name} ke bawaan?`}
        description="Seluruh perubahan pada paket ini dibuang, dan nilainya kembali seperti yang tertulis di kode."
        confirmLabel="Ya, kembalikan"
      />
    </div>
  );
};
